import { supabase } from '../lib/admin-supabase'

export interface Emenda {
  id: string
  titulo: string
  categoria?: string
  resumo?: string
  descricao_completa?: string
  valor_emenda?: number
  municipio_beneficiado?: string
  estado?: string
  status: 'ativo' | 'rascunho' | 'inativo'
  imagem_capa?: string
  destaque?: boolean
  created_at: string
  updated_at: string
}

export interface EmendaDocumento {
  id: string
  emenda_id: string
  nome_documento: string
  arquivo_url: string
  tipo_documento: string
  created_at: string
}

export interface EmendaCompleta extends Emenda {
  documentos: EmendaDocumento[]
}

export interface CriarEmendaData {
  titulo: string
  categoria?: string
  resumo?: string
  descricao_completa?: string
  valor_emenda?: number
  municipio_beneficiado?: string
  estado?: string
  status?: 'ativo' | 'rascunho' | 'inativo'
  destaque?: boolean
  imagemCapa?: File | null
  documentos?: File[]
}

// ==========================================
// FUNÇÕES RÚBLICAS (PARA O SITE)
// ==========================================

export const listarEmendas = async (params: { limit?: number; status?: string; buscar?: string } = {}) => {
  try {
    const { limit = 100, status = null, buscar = null } = params

    let query = supabase
      .from('emendas')
      .select(`
        *,
        emenda_documentos(id, nome_documento, arquivo_url)
      `)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (status) query = query.eq('status', status)
    if (buscar) query = query.or(`titulo.ilike.%${buscar}%,resumo.ilike.%${buscar}%`)

    const { data, error } = await query
    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Erro ao listar emendas:', error)
    throw error
  }
}

export const listarEmendasPublicas = async () => {
  return listarEmendas({ status: 'ativo' })
}

export const buscarEmendaPublica = async (id: string) => {
  try {
    const { data: emenda, error: emendaError } = await supabase
      .from('emendas')
      .select('*')
      .eq('id', id)
      .single()

    if (emendaError) throw emendaError

    const { data: docs, error: docsError } = await supabase
      .from('emenda_documentos')
      .select('*')
      .eq('emenda_id', id)

    return {
      ...emenda,
      documentos: docs || []
    }
  } catch (error) {
    console.error('Erro ao buscar detalhes da emenda:', error)
    throw error
  }
}

// ==========================================
// FUNÇÕES ADMINISTRATIVAS (PAINEL ADMIN)
// ==========================================

export const verificarAdmin = async (): Promise<boolean> => {
  const userStr = localStorage.getItem('admin_user')
  if (!userStr) return false
  try {
    const user = JSON.parse(userStr)
    return user.role === 'admin'
  } catch {
    return false
  }
}

export const criarEmenda = async (emendaData: CriarEmendaData) => {
  try {
    let imagemCapaUrl: string | null = null

    if (emendaData.imagemCapa) {
      const fileExt = emendaData.imagemCapa.name.split('.').pop()
      const fileName = `capa-${Date.now()}.${fileExt}`
      const { error: uploadError } = await supabase.storage
        .from('emendas-imagens')
        .upload(fileName, emendaData.imagemCapa)
      if (uploadError) throw uploadError
      const { data: { publicUrl } } = supabase.storage.from('emendas-imagens').getPublicUrl(fileName)
      imagemCapaUrl = publicUrl
    }

    const { data: emenda, error: emendaError } = await supabase
      .from('emendas')
      .insert({
        titulo: emendaData.titulo,
        categoria: emendaData.categoria || null,
        resumo: emendaData.resumo || null,
        descricao_completa: emendaData.descricao_completa || null,
        valor_emenda: emendaData.valor_emenda || null,
        municipio_beneficiado: emendaData.municipio_beneficiado || null,
        estado: emendaData.estado || null,
        status: emendaData.status || 'ativo',
        imagem_capa: imagemCapaUrl,
        destaque: emendaData.destaque || false
      })
      .select()
      .single()

    if (emendaError) throw emendaError

    if (emendaData.documentos && emendaData.documentos.length > 0) {
      for (const documento of emendaData.documentos) {
        const fileExt = documento.name.split('.').pop()
        const fileName = `doc-${emenda.id}-${Date.now()}.${fileExt}`
        const { error: docUploadError } = await supabase.storage
          .from('emendas-documentos')
          .upload(fileName, documento)
        if (docUploadError) throw docUploadError
        const { data: { publicUrl } } = supabase.storage.from('emendas-documentos').getPublicUrl(fileName)
        await supabase.from('emenda_documentos').insert({
          emenda_id: emenda.id,
          nome_documento: documento.name,
          arquivo_url: publicUrl,
          tipo_documento: 'pdf'
        })
      }
    }
    return { success: true, emendaId: emenda.id }
  } catch (error) {
    console.error('Erro ao criar emenda:', error)
    throw error
  }
}

export const buscarEmendaCompleta = async (emendaId: string): Promise<EmendaCompleta> => {
  try {
    const { data, error } = await supabase
      .from('emendas')
      .select('*, emenda_documentos(*)')
      .eq('id', emendaId)
      .single()
    if (error) throw error
    return { ...data, documentos: data.emenda_documentos || [] } as EmendaCompleta
  } catch (error) {
    console.error('Erro ao buscar emenda:', error)
    throw error
  }
}

export const atualizarEmenda = async (emendaId: string, emendaData: Partial<CriarEmendaData>) => {
  try {
    const updateData: any = {
      titulo: emendaData.titulo,
      categoria: emendaData.categoria,
      resumo: emendaData.resumo,
      descricao_completa: emendaData.descricao_completa,
      valor_emenda: emendaData.valor_emenda,
      municipio_beneficiado: emendaData.municipio_beneficiado,
      estado: emendaData.estado,
      status: emendaData.status,
      destaque: emendaData.destaque,
      updated_at: new Date().toISOString()
    }

    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key])

    if (emendaData.imagemCapa instanceof File) {
      const fileExt = emendaData.imagemCapa.name.split('.').pop()
      const fileName = `capa-${emendaId}-${Date.now()}.${fileExt}`
      const { error: uploadError } = await supabase.storage.from('emendas-imagens').upload(fileName, emendaData.imagemCapa)
      if (!uploadError) {
        const { data: { publicUrl } } = supabase.storage.from('emendas-imagens').getPublicUrl(fileName)
        updateData.imagem_capa = publicUrl
      }
    }

    const { data, error } = await supabase.from('emendas').update(updateData).eq('id', emendaId).select().single()
    if (error) throw error

    if (emendaData.documentos && emendaData.documentos.length > 0) {
      for (const documento of emendaData.documentos) {
        const fileName = `doc-${emendaId}-${Date.now()}-${documento.name}`
        const { error: docUploadError } = await supabase.storage.from('emendas-documentos').upload(fileName, documento)
        if (!docUploadError) {
          const { data: { publicUrl } } = supabase.storage.from('emendas-documentos').getPublicUrl(fileName)
          await supabase.from('emenda_documentos').insert({
            emenda_id: emendaId,
            nome_documento: documento.name,
            arquivo_url: publicUrl,
            tipo_documento: 'pdf'
          })
        }
      }
    }
    return data
  } catch (error) {
    console.error('Erro ao atualizar emenda:', error)
    throw error
  }
}

export const excluirEmenda = async (emendaId: string) => {
  try {
    const { error } = await supabase.from('emendas').delete().eq('id', emendaId)
    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Erro ao excluir emenda:', error)
    throw error
  }
}

export const obterEstatisticas = async () => {
  try {
    const { data: emendas, error } = await supabase
      .from('emendas')
      .select('status, valor_emenda, created_at')

    if (error) throw error

    const now = new Date()
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)

    const stats = {
      total: emendas.length,
      ativas: emendas.filter(e => e.status === 'ativo').length,
      inativas: emendas.filter(e => e.status === 'inativo').length,
      rascunhos: emendas.filter(e => e.status === 'rascunho').length,
      valorTotal: emendas.reduce((acc, e) => acc + (Number(e.valor_emenda) || 0), 0),
      criadasEsteMes: emendas.filter(e => new Date(e.created_at) >= firstDayOfMonth).length
    }

    return stats
  } catch (error) {
    console.error('Erro ao obter estatísticas:', error)
    throw error
  }
}
